import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * (Trello 卡 6aab90b1e1dc16fd9ba865c0, 辉哥 9/17 15:01 拍板)
 * Hero image 强剪裁 16:9 modal (简化版)
 *
 * 辉哥 SOP:
 *   - 用户上传图 → 加载到 Image → 计算居中 16:9 区域 (取最大中心裁切)
 *   - 绘制到 canvas (16:9 比例) → toBlob → 上传到 COS
 *   - 不要弹比例选择 modal (用户不可改, 1 号方案)
 *   - 不要用 Cropper.js 第三方库
 *
 * (辉哥 9/17 22:04 拍板):
 *   - 顺带修 r.18j 漏掉的 onApply() L191-192 `this.props.onUpload` → `this.attrs.onUpload`
 *     (Flarum 2.0 Component attrs 存 this.attrs 不是 this.props). 这是辉哥 9/17 21:53
 *     反馈 "上传头图后没出现剪裁功能" 的真因 — modal 弹了 + 选择 + 点应用都 OK, 但
 *     callback this.props.onUpload 永远 undefined, composer 永远收不到 url.
 *
 * 设计:
 *   - 用户选本地图片 (单选 input[type=file])
 *   - modal 显示原图 + 居中 16:9 预览 canvas
 *   - 不可拖框、不可改比例
 *   - 点 "应用" → canvas.toBlob (image/jpeg, 0.9 质量) → uploadToCOS
 *   - uploadToCOS 走本扩展的共享上传方法 common/helpers/uploadFile
 *     (Phase D2: 不再硬编码腾讯云, 随 storage_provider 切 COS / 七牛 / 本地)
 *   - 成功后 callback props.onUpload(cdnUrl) 给 HeroImageUpload → 写 composer.data
 *
 * 边界:
 *   - 文件大小限制 5MB (cos signature policy conditions 不强制, 客户端 check)
 *   - 仅接受 image/jpeg, image/png, image/webp
 *   - 输出统一 image/jpeg 0.9 (压缩率好, 兼容性好)
 *
 * 限制:
 *   - 简化版只做居中 16:9 强剪裁, 未来如果需要 1:1 等比例再加 (V1 设计图)
 */

import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
// Phase D2: 裁剪后的图片改走**本扩展自己的**共享上传方法 (common/helpers/uploadFile),
// 于是 HeroImageCropModal 也不再硬编码 COS —— 随 storage_provider 自动切三档。
// ⚠️ 这里刻意用**相对路径**而不是 ext:ziiven/ziven-core/... ——
//    ext: 的 namespace 正则会把 ziiven/ziven-core 拼成 namespace「ziiven-ziven-core」,
//    自己 import 自己走 registry 是多余一跳, 相对路径在同一个 bundle 里直连更快。
import { uploadFile } from '../../common/helpers/uploadFile';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const TARGET_RATIO = 16 / 9; // 16:9
const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.9;
export default class HeroImageCropModal extends Modal {
  /**
   * props:
   *   - currentUrl: 已存在的 hero image URL (string | null)
   *   - onUpload: (newCdnUrl) => void
   *
   * (22:36 辉哥 9/17 22:31 反馈 "剪裁区域移动不了, 不能按 16:9 比例放大缩小" 真因修):
   *   之前 r.18l 写死 cropRegion = imagePixelCentered16x9 (强剪裁不可改), 辉哥改主意要完整功能:
   *   - crop 状态: { x, y, w, h } 0~1 比例相对 stage (vendor zpc ImageCropModal 同款)
   *   - dragMode: 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br'
   *   - image: HTMLImageElement (用于 canvas drawImage 取 naturalWidth/Height)
   *   - stageRef / selectionRef: DOM 引用 (getBoundingClientRect 算 stage 实际像素)
   *   - 默认 crop 居中 16:9: 80% wide × 80% * 9/16 = 45% high
   *   - move: 整体平移 (限制在 stage 内)
   *   - 4 角 resize: 锁定 16:9 比例 (dragMode === 'resize-xx' 时 newW/newH 比例锁)
   */
  constructor(props) {
    super(props);
    this.imageDataUrl = null;
    this.image = null; // HTMLImageElement
    this.imageWidth = 0; // naturalWidth
    this.imageHeight = 0; // naturalHeight
    // r.18l (22:58 第十四轮): crop 必须 3 字段 { x, y, w }, h 完全由 CSS aspect-ratio: 16/9 决定
    //   之前 r.18l 第八/十一轮我尝试存 crop.h 用作拖拽边界, 但:
    //   1. CSS aspect-ratio: 16/9 决定 visual H = visual W × 9/16 (按 width 算)
    //   2. crop.h 是 imageArea 内归一化值 (跟 stage 比), 跟 CSS visual H 数值不一致 (取决于 stage ratio)
    //   3. getCropPixelRect 用 crop.h × stageH × (imgH/drawH) 算 canvas H, 但 selection 视觉 H 是 crop.w × stageW × 9/16
    //      → canvas 输出 ≠ selection 视觉 (辉哥 9/17 22:58 反馈 "最后剪裁出来的图片跟当时选择剪裁的区域不一样")
    //   修法: 彻底删 crop.h, CSS aspect-ratio 是视觉高度唯一来源, getCropPixelRect 用 visual_W × 9/16 反推 srcH
    //   move/resize 边界限制用 imageArea (不是 stage), 用 visual_H (= crop.w × stageW × 9/16) 算
    this.crop = {
      x: 0.1,
      y: 0.275,
      w: 0.45
    }; // 1:1 image in 16:9 stage 占位, initDefaultCrop 重算
    this.cropInitialized = false; // initDefaultCrop 只初始化一次, 用户拖动后保留
    this.dragMode = null; // 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br'
    this.dragStart = null; // { crop, mouseX, mouseY, stageW, stageH, imgArea: {x,y,w,h} }
    this.stageRef = null; // DOM ref to .HeroImageCropModal-preview-stage
    this.processing = false;
  }
  className() {
    return 'HeroImageCropModal Modal--large';
  }
  title() {
    return app.translator.trans('ziven-community-core.forum.hero_image.crop_modal_title');
  }

  /**
   * r.18l (22:36): 绑全局 mousemove/mouseup listener 引用 (给 addEventListener 用)
   * mithril 2.x 不代理 pointer events, 必须手动 bind (vendor zpc ImageCropModal #365 SOP)
   */
  oninit(vnode) {
    super.oninit(vnode);
    this.boundMouseMove = e => this.onMouseMove(e);
    this.boundMouseUp = e => this.onMouseUp(e);
  }

  /**
   * r.18l (22:42 第八轮): attachDragHandlers — mithril 2.x 不代理 mousedown/pointerdown JSX 属性
   *   辉哥 zpc 9/17 22:02 亲测 bug: 原 onmousedown={...} JSX 属性被 mithril 静默丢弃, 拖拽完全失效
   *   修法: oncreate + addEventListener pointer events (vendor zpc attachDragHandlers L625-680 SOP)
   *   - stage + 4 个 handle + selection 都用 addEventListener 绑 pointerdown
   *   - stage: 点空白处触发 move (整体平移, zpc Phase 1 行为)
   *   - selection: 点选区内部触发 move (整体平移)
   *   - 4 个 handle: 点 handle 触发 resize (resize-tl/tr/bl/br)
   *   - boundMouseMove/MouseUp 在 onMouseDown 调 addEventListener 绑 document.body
   *
   * 关键: 不依赖 ref callback (ref={el => this.stageRef = el}), ref callback 在 oncreate 时
   *       不一定 fire (Flarum stackModal=true 隐藏 modal 时不可靠).
   *       用 querySelector('.HeroImageCropModal-preview-stage') 等 — DOM 元素已经在.
   * 多次 RAF 保险: 用 requestAnimationFrame 等下一帧再 attach, 确保 DOM 已渲染.
   */
  oncreate(vnode) {
    super.oncreate(vnode);
    requestAnimationFrame(() => this.attachDragHandlers());
  }

  /**
   * r.18l (22:42 第八轮): modal 关闭时清理全局 listener (避免 memory leak)
   *   + 清理 pointerdown listener (selection / handle / stage)
   */
  onremove(vnode) {
    this.onMouseUp();
    if (this.element) {
      const stageEl = this.element.querySelector('.HeroImageCropModal-preview-stage');
      const selectionEl = this.element.querySelector('.HeroImageCropModal-preview-selection');
      const handleEls = {
        tl: this.element.querySelector('.crop-handle.tl'),
        tr: this.element.querySelector('.crop-handle.tr'),
        bl: this.element.querySelector('.crop-handle.bl'),
        br: this.element.querySelector('.crop-handle.br')
      };
      if (stageEl && this.boundStagePointerDown) stageEl.removeEventListener('pointerdown', this.boundStagePointerDown);
      if (selectionEl && this.boundSelectionPointerDown) selectionEl.removeEventListener('pointerdown', this.boundSelectionPointerDown);
      for (const mode in handleEls) {
        const el = handleEls[mode];
        if (el && this.boundHandlePointerDown && this.boundHandlePointerDown[mode]) {
          el.removeEventListener('pointerdown', this.boundHandlePointerDown[mode]);
        }
      }
    }
    super.onremove(vnode);
  }

  /**
   * r.18l (22:42 第九轮补): onupdate 二次保险 (辉哥 zpc SOP)
   *   oncreate 时 imageDataUrl 还是 null (用户还没选图), stage 元素不存在,
   *   querySelector('.HeroImageCropModal-preview-stage') 返 null, attachDragHandlers 啥也没绑.
   *   用户选图后 onFileChosen 设 imageDataUrl + m.redraw 触发 onupdate, 此时 stage 才渲染.
   *   onupdate 里如果还没绑 (boundStagePointerDown undefined), 再 RAF 一次 attachDragHandlers.
   *   防止重复绑: attachDragHandlers 内部检查 boundStagePointerDown 标志, 已绑直接 return.
   */
  onupdate(vnode) {
    super.onupdate(vnode);
    if (!this.boundStagePointerDown) {
      requestAnimationFrame(() => this.attachDragHandlers());
    }
  }

  /**
   * r.18l (22:42 第八轮): 绑定 pointerdown 到 stage / selection / 4 个 handle
   * 模仿 vendor zpc ImageCropModal.attachDragHandlers (L625-680)
   * 用 pointer events 统一 mouse + touch (现代浏览器都支持)
   *
   * 同时调 initDefaultCrop (按 stage 实际宽高算 16:9 居中 crop)
   *   - vendor zpc initDefaultCrop (L579-595): 取 stage 短边 60% 作为正方形边长, 居中
   *   - ziven-core 简化: TARGET_RATIO = 16/9 (横宽选区), 取 stage 短边 80% 作为高
   *     visualW = baseSide × 16/9, visualH = baseSide, 居中
   *     crop.x = (stageW - visualW) / 2 / stageW
   *     crop.y = (stageH - visualH) / 2 / stageH
   *     crop.w = visualW / stageW
   *     (h 由 CSS aspect-ratio 自动算)
   *   - 只初始化一次 (cropInitialized 标志), 用户拖动后保留
   */
  attachDragHandlers() {
    if (!this.element) return;
    const root = this.element;
    const stageEl = root.querySelector('.HeroImageCropModal-preview-stage');
    const selectionEl = root.querySelector('.HeroImageCropModal-preview-selection');
    const handleEls = {
      tl: root.querySelector('.crop-handle.tl'),
      tr: root.querySelector('.crop-handle.tr'),
      bl: root.querySelector('.crop-handle.bl'),
      br: root.querySelector('.crop-handle.br')
    };

    // 同步 this.stageRef (applyCrop 用)
    this.stageRef = stageEl;
    if (!stageEl) return;

    // 按 stage 实际像素算 16:9 居中 crop (在 attachDragHandlers 末尾 — stageRef 已就绪)
    this.initDefaultCrop();

    // 防止重复绑定
    if (this.boundStagePointerDown) return;

    // stage: 点空白处触发 move (整体平移)
    this.boundStagePointerDown = e => {
      // 只在 mousedown 直接点在 stage (非 selection 子元素) 上时触发 move
      // stage 内 img 也算 stage, zpc SOP
      if (e.target === stageEl || e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('move', e);
      }
    };
    stageEl.addEventListener('pointerdown', this.boundStagePointerDown);

    // selection: 点选区内部触发 move (整体平移)
    if (selectionEl) {
      this.boundSelectionPointerDown = e => {
        // 如果点在 corner handle 上 — 让 handle listener 处理 (event 不会冒泡到 selection
        // 因为 handle 是 selection 子元素, 但 listener 是绑在 handle 上的, 会先触发)
        if (e.target.classList.contains('crop-handle')) {
          return;
        }
        // 否则 (点在选区内部) — 触发 move
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('move', e);
      };
      selectionEl.addEventListener('pointerdown', this.boundSelectionPointerDown);
    }

    // 4 个 handle: 点 handle 触发 resize
    this.boundHandlePointerDown = {};
    for (const mode in handleEls) {
      const el = handleEls[mode];
      if (!el) continue;
      this.boundHandlePointerDown[mode] = e => {
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('resize-' + mode, e);
      };
      el.addEventListener('pointerdown', this.boundHandlePointerDown[mode]);
    }
  }

  /**
   * r.18l (22:42 第八轮): 按 stage 实际宽高算 16:9 居中 crop
   *   vendor zpc initDefaultCrop (L579-595) 简化版, TARGET_RATIO = 16/9 (横宽选区)
   *   - 取 stage 短边 80% 作为高
   *   - visualW = baseSide × 16/9, visualH = baseSide
   *   - 居中: crop.x = (stageW - visualW) / 2 / stageW, crop.y 类似
   *   - crop.w = visualW / stageW
   *   - (crop.h 不存, 由 CSS aspect-ratio 决定)
   *   - 只初始化一次 (cropInitialized), 用户拖动后保留
   */
  initDefaultCrop() {
    if (this.cropInitialized) return;
    if (!this.stageRef || !this.image) return;
    const stageRect = this.stageRef.getBoundingClientRect();
    const stageW = stageRect.width;
    const stageH = stageRect.height;
    if (stageW <= 0 || stageH <= 0) return;

    // r.18l (22:55 第十二轮): 模仿 vendor zpc ImageCropModal.getCropPixelRect (L113-152) 算法
    //   image 在 stage 内 object-fit:contain 渲染, 算 image 实际显示区
    // r.18l (22:58 第十四轮): crop 改 3 字段 { x, y, w }, h 由 CSS aspect-ratio: 16/9 决定
    //   getCropPixelRect 用 visual_W × 9/16 算 canvas 输出 (跟 selection 视觉一致)
    //   move/resize 边界限制改 imageArea (不是 stage), 用 visual_H 算
    const imgArea = this.getImageAreaBounds();
    const drawW = imgArea.w;
    const drawH = imgArea.h;
    const offsetX = imgArea.x;
    const offsetY = imgArea.y;

    // 在 imageArea 内取 16:9 矩形 (80% imageArea, 留 20% 余量让用户可拖)
    const imgW = this.image.naturalWidth;
    const imgH = this.image.naturalHeight;
    const imgRatio = imgW / imgH;
    const selScale = 0.8;
    let selW_visual;
    if (TARGET_RATIO > imgRatio) {
      // 16/9 > 1:1 image, 选区按 imageArea 高 × 0.8 算, 选区宽 = 高 × 16/9, clamp 到 image 宽
      const selH_visual = drawH * selScale;
      selW_visual = selH_visual * TARGET_RATIO;
      if (selW_visual > drawW) selW_visual = drawW * selScale; // 1:1 image 选区宽度也 clamp
    } else {
      // 选区按 imageArea 宽 × 0.8 算, 选区高 = 宽 / 16/9, clamp 到 image 高
      selW_visual = drawW * selScale;
    }

    // 居中于 imageArea, crop 只有 { x, y, w } (h 由 CSS aspect-ratio 自动算)
    // selection 视觉 H = visual_W × 9/16 = (crop.w × stageW) × 9/16
    // 对于 selection visual H 居中于 imageArea: top_visual = offsetY + (drawH - selH_visual) / 2
    // selH_visual = visual_W × 9/16 = (selW_visual) × 9/16
    const selH_visual = selW_visual * (9 / 16);
    this.crop = {
      x: (offsetX + (drawW - selW_visual) / 2) / stageW,
      y: (offsetY + (drawH - selH_visual) / 2) / stageH,
      w: selW_visual / stageW
      // 不存 h, h 由 CSS aspect-ratio 决定 (跟视觉一致)
    };
    this.cropInitialized = true;
    m.redraw();
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "HeroImageCropModal-body"
    }, !this.imageDataUrl && this.renderFileInput(), this.imageDataUrl && this.renderCropPreview()));
  }
  renderFileInput() {
    return m("div", {
      className: "HeroImageCropModal-input"
    }, m("p", {
      className: "HeroImageCropModal-hint"
    }, app.translator.trans('ziven-community-core.forum.hero_image.crop_hint')), m("label", {
      className: "Button Button--primary HeroImageCropModal-choose"
    }, m("i", {
      className: "fas fa-folder-open"
    }), " ", app.translator.trans('ziven-community-core.forum.hero_image.choose_file'), m("input", {
      type: "file",
      accept: "image/jpeg,image/png,image/webp",
      onchange: e => this.onFileChosen(e),
      style: {
        display: 'none'
      }
    })));
  }
  renderCropPreview() {
    // r.18l (22:42 第八轮): selectionStyle 只设 left/top/width, height 由 CSS aspect-ratio: 16/9 自动算
    //   这样 width 80% + aspect-ratio 16/9 = height = 80% × (stage_w / stage_h) × 9/16 实际像素高度
    //   之前 inline style height: 45% 跟 stage 实际像素错位 → selection 不是 16:9
    const selectionStyle = {
      left: this.crop.x * 100 + '%',
      top: this.crop.y * 100 + '%',
      width: this.crop.w * 100 + '%'
    };
    return m("div", {
      className: "HeroImageCropModal-preview"
    }, m("p", {
      className: "HeroImageCropModal-hint"
    }, app.translator.trans('ziven-community-core.forum.hero_image.preview_hint')), m("div", {
      className: "HeroImageCropModal-preview-stage",
      ref: el => {
        this.stageRef = el;
      }
    }, m("img", {
      className: "HeroImageCropModal-preview-image",
      src: this.imageDataUrl,
      alt: "",
      draggable: false
    }), m("div", {
      className: "HeroImageCropModal-preview-selection",
      style: selectionStyle
    }, m("div", {
      className: "crop-rule h-top"
    }), m("div", {
      className: "crop-rule h-mid"
    }), m("div", {
      className: "crop-rule h-bot"
    }), m("div", {
      className: "crop-rule v-l"
    }), m("div", {
      className: "crop-rule v-m"
    }), m("div", {
      className: "crop-rule v-r"
    }), m("div", {
      className: "crop-handle tl"
    }), m("div", {
      className: "crop-handle tr"
    }), m("div", {
      className: "crop-handle bl"
    }), m("div", {
      className: "crop-handle br"
    }))), m("div", {
      className: "HeroImageCropModal-actions"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.processing,
      disabled: this.processing,
      onclick: () => this.onApply()
    }, app.translator.trans('ziven-community-core.forum.hero_image.apply')), m(Button, {
      className: "Button",
      disabled: this.processing,
      onclick: () => this.hide()
    }, app.translator.trans('ziven-community-core.forum.hero_image.cancel'))));
  }

  // ── 事件处理 ─────────────────────────────────────────

  async onFileChosen(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // 1) 客户端校验
    if (file.size > MAX_FILE_SIZE) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.hero_image.error_file_too_large', {
        maxSize: '5MB'
      }));
      e.target.value = '';
      return;
    }
    if (ALLOWED_TYPES.indexOf(file.type) === -1) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.hero_image.error_file_type'));
      e.target.value = '';
      return;
    }

    // 2) 读取为 DataURL + 加载 HTMLImageElement (供 canvas drawImage + getBoundingClientRect)
    try {
      const dataUrl = await this.readFileAsDataUrl(file);
      const img = await this.loadImageElement(dataUrl);
      this.imageDataUrl = dataUrl;
      this.image = img;
      this.imageWidth = img.naturalWidth;
      this.imageHeight = img.naturalHeight;
      // crop 保持 constructor 默认值 (居中 16:9 80%×45%)
      // image 切换时不需要重置 crop (用户已选好的剪裁位置保留)

      m.redraw();
    } catch (err) {
      console.error('[HeroImageCropModal] Failed to load image:', err);
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.hero_image.error_load'));
      e.target.value = '';
    }
  }
  async onApply() {
    if (!this.imageDataUrl || !this.image) return;
    if (this.processing) return;
    this.processing = true;
    m.redraw();
    try {
      // 1) 按 crop 选区从 image 截取 → blob (用 getCropPixelRect 算原图坐标)
      const blob = await this.applyCrop();

      // 2) 上传 COS
      const cdnUrl = await this.uploadToCOS(blob);

      // 3) callback (Flarum 2.0: attrs 存 this.attrs, 不是 1.8 时代的 this.props)
      if (typeof this.attrs.onUpload === 'function') {
        this.attrs.onUpload(cdnUrl);
      }
      this.hide();
    } catch (err) {
      console.error('[HeroImageCropModal] Apply failed:', err);
      app.alerts.show({
        type: 'error'
      }, err.message || app.translator.trans('ziven-community-core.forum.hero_image.error_upload'));
      this.processing = false;
      m.redraw();
    }
  }

  // ── 拖拽/缩放 ─────────────────────────────────────────

  /**
   * r.18l (22:42 第八轮): pointerdown — 启动拖拽 (move 或 4 角 resize)
   * mithril 2.x 不代理 pointerdown, 已在 attachDragHandlers 用 addEventListener 绑到
   * stage / selection / 4 个 handle, 这里只启动 drag 状态 + 绑全局 pointermove/pointerup
   * (zpc ImageCropModal #365 SOP, 改用 pointer events 统一 mouse + touch)
   */
  onMouseDown(mode, e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!this.stageRef) return;

    // pointer event clientX/Y 统一 (mouse + touch 都用同一个字段)
    const clientX = e && (e.clientX !== undefined ? e.clientX : 0);
    const clientY = e && (e.clientY !== undefined ? e.clientY : 0);
    const stageRect = this.stageRef.getBoundingClientRect();
    // r.18l (22:58 第十四轮): 存 imageArea 边界 (像素) 用于 move/resize clamp
    //   selection 完全在 imageArea 内, 不能超出 image 范围 (辉哥 9/17 22:53 反馈)
    const imgArea = this.getImageAreaBounds();
    // selection 视觉 H = visual_W × 9/16 (CSS aspect-ratio 16/9), 用于边界计算
    const selW_visual = this.crop.w * stageRect.width;
    const selH_visual = selW_visual * (9 / 16);
    this.dragMode = mode;
    this.dragStart = {
      crop: _objectSpread({}, this.crop),
      mouseX: clientX,
      mouseY: clientY,
      stageW: stageRect.width,
      stageH: stageRect.height,
      imgArea,
      // { x, y, w, h } image 实际显示区像素 (在 stage 内)
      selW_visual,
      // selection 视觉宽 (像素, resize 模式用)
      selH_visual // selection 视觉高 (像素, 边界限制用)
    };

    // 全局跟踪 (移到 body 才能跨元素跟踪)
    if (typeof document !== 'undefined') {
      document.addEventListener('pointermove', this.boundMouseMove);
      document.addEventListener('pointerup', this.boundMouseUp);
      document.addEventListener('pointercancel', this.boundMouseUp);
    }
  }

  /**
   * 鼠标移动 — 更新 crop
   * r.18l (22:36): 锁定 16:9 比例 (move 模式自由移动, resize 模式 newW/newH 比例锁)
   */
  onMouseMove(e) {
    if (!this.dragMode || !this.dragStart) return;
    if (e) {
      e.preventDefault();
    }
    const clientX = e && (e.clientX !== undefined ? e.clientX : e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e && (e.clientY !== undefined ? e.clientY : e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    const dx = (clientX - this.dragStart.mouseX) / this.dragStart.stageW;
    const dy = (clientY - this.dragStart.mouseY) / this.dragStart.stageH;
    const startCrop = this.dragStart.crop;
    const startW = startCrop.w;
    const imgArea = this.dragStart.imgArea;
    const stageW = this.dragStart.stageW;
    const stageH = this.dragStart.stageH;
    const newCrop = _objectSpread({}, startCrop);
    if (this.dragMode === 'move') {
      // 整体平移 — selection 视觉 W/H 固定 (由 startW 决定), clamp 到 imageArea 内
      const selW_visual = startW * stageW;
      const selH_visual = selW_visual * (9 / 16);
      const minX = imgArea.x / stageW;
      const maxX = (imgArea.x + imgArea.w - selW_visual) / stageW;
      const minY = imgArea.y / stageH;
      const maxY = (imgArea.y + imgArea.h - selH_visual) / stageH;
      newCrop.x = Math.max(minX, Math.min(maxX, startCrop.x + dx));
      newCrop.y = Math.max(minY, Math.min(maxY, startCrop.y + dy));
      // w 不变, 不存 h (CSS aspect-ratio 决定)
    } else {
      // 4 角 resize — 16:9 锁定由 CSS aspect-ratio 决定 (width → height 反推)
      // 4 角分别算 newX/newY/newW (基于 dx/dy)
      let newX = startCrop.x;
      let newY = startCrop.y;
      let newW = startW;
      if (this.dragMode === 'resize-tl') {
        newX = startCrop.x + dx;
        newY = startCrop.y + dy;
        newW = startW - (newX - startCrop.x);
      } else if (this.dragMode === 'resize-tr') {
        newY = startCrop.y + dy;
        newW = startW + dx;
      } else if (this.dragMode === 'resize-bl') {
        newX = startCrop.x + dx;
        newW = startW - (newX - startCrop.x);
      } else if (this.dragMode === 'resize-br') {
        newW = startW + dx;
      }

      // 16:9 锁定 (CSS aspect-ratio, visual H = visual W × 9/16)
      // selection 不能超出 imageArea:
      //   - visual W ≤ imageArea.w
      //   - visual H ≤ imageArea.h, 即 visual W ≤ imageArea.h / (9/16)
      //   - 综合: visual W ≤ min(imageArea.w, imageArea.h / (9/16))
      const minNewW_visual = stageW * 0.05; // 归一化 5% stage width (避免太小)
      const maxNewW_visual = Math.min(imgArea.w, imgArea.h / (9 / 16));
      const newW_visual_clamped = Math.max(minNewW_visual, Math.min(maxNewW_visual, newW * stageW));
      newW = newW_visual_clamped / stageW;

      // selection 不能超出 imageArea 边界 (left ≥ imgArea.x, right ≤ imgArea.x + imgArea.w)
      const newW_visual = newW * stageW;
      const newH_visual = newW_visual * (9 / 16);
      const minX = imgArea.x / stageW;
      const maxX = (imgArea.x + imgArea.w - newW_visual) / stageW;
      const minY = imgArea.y / stageH;
      const maxY = (imgArea.y + imgArea.h - newH_visual) / stageH;
      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));
      newCrop.x = newX;
      newCrop.y = newY;
      newCrop.w = newW;
      // 不存 h (CSS aspect-ratio 决定)
    }
    this.crop = newCrop;
    m.redraw();
  }

  /**
   * 鼠标松开 — 结束拖拽 (r.18l 22:42 第八轮 改 pointer events)
   */
  onMouseUp() {
    this.dragMode = null;
    this.dragStart = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('pointermove', this.boundMouseMove);
      document.removeEventListener('pointerup', this.boundMouseUp);
      document.removeEventListener('pointercancel', this.boundMouseUp);
    }
  }

  // ── 工具方法 ─────────────────────────────────────────

  /**
   * r.18l (22:58 第十四轮): 算 image 在 stage 内 object-fit:contain 渲染的实际显示区 (像素)
   *   返回 { x, y, w, h } 是 stage 容器坐标系内的像素矩形
   *   1:1 image in 16:9 stage: drawW = drawH = stageH, offsetX = (stageW - stageH) / 2, offsetY = 0
   *   21:9 image in 16:9 stage: drawW = stageW, drawH = stageW / (21/9) ≈ stageW × 0.43, 上下 letterbox
   */
  getImageAreaBounds() {
    if (!this.image || !this.stageRef) return {
      x: 0,
      y: 0,
      w: 0,
      h: 0
    };
    const stageRect = this.stageRef.getBoundingClientRect();
    const stageW = stageRect.width;
    const stageH = stageRect.height;
    if (stageW <= 0 || stageH <= 0) return {
      x: 0,
      y: 0,
      w: 0,
      h: 0
    };
    const imgW = this.image.naturalWidth;
    const imgH = this.image.naturalHeight;
    const imgRatio = imgW / imgH;
    const stageRatio = stageW / stageH;
    let drawW, drawH;
    if (imgRatio > stageRatio) {
      drawW = stageW;
      drawH = stageW / imgRatio;
    } else {
      drawH = stageH;
      drawW = stageH * imgRatio;
    }
    const offsetX = (stageW - drawW) / 2;
    const offsetY = (stageH - drawH) / 2;
    return {
      x: offsetX,
      y: offsetY,
      w: drawW,
      h: drawH
    };
  }

  /**
   * r.18l (22:58 第十四轮): selection 视觉尺寸 (像素)
   *   visual_W = crop.w × stageW
   *   visual_H = visual_W × 9/16 (CSS aspect-ratio: 16/9, width/height = 16/9, height = width / (16/9) = width × 9/16)
   *   注意: 跟 crop.h × stageH 不一定一致 (取决于 stage ratio)
   */
  getSelectionVisualSize() {
    const stageRect = this.stageRef.getBoundingClientRect();
    const visualW = this.crop.w * stageRect.width;
    const visualH = visualW * (9 / 16); // CSS aspect-ratio 16/9
    return {
      w: visualW,
      h: visualH
    };
  }
  readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * 加载 dataURL → HTMLImageElement (供 canvas drawImage 用, 不用再 new Image())
   * 替代之前 r.18 写的 loadImageSize (只返 width/height, 剪裁时还要重新 new Image)
   */
  loadImageElement(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * r.18l (22:58 第十四轮): 按 crop (3 字段 { x, y, w }) + image natural size 算出原图坐标系剪裁源矩形
   *
   * 关键修正 — selection 视觉 H 完全由 CSS aspect-ratio: 16/9 决定:
   *   visual_W = crop.w × stageW
   *   visual_H = visual_W × 9/16  (CSS aspect-ratio 16/9)
   * 之前 r.18l 第十一轮之前用 crop.h × stageH × (imgH/drawH) 算 canvas 输出 H,
   *   但 crop.h 是 initDefaultCrop 按 imageArea 算的归一化值, 跟 CSS visual H 数值不一致
   *   → canvas 输出跟 selection 视觉不对应 (辉哥 9/17 22:58 反馈)
   *
   * 模仿 vendor zpc ImageCropModal.getCropPixelRect (Phase 1):
   *   - image 在 stage 中按 object-fit: contain 渲染
   *   - 视觉选区是相对 stage 的 0~1, 要先平移到 image 实际渲染区 (减 offsetX/offsetY)
   *     再按 (imgW/drawW) 缩放回原图坐标系
   *
   * 返回 srcX/srcY/srcW/srcH (原图坐标, 用于 canvas drawImage)
   */
  getCropPixelRect(stageW, stageH) {
    const imgW = this.image.naturalWidth;
    const imgH = this.image.naturalHeight;
    const imgRatio = imgW / imgH;
    const stageRatio = stageW / stageH;
    let drawW, drawH;
    if (imgRatio > stageRatio) {
      drawW = stageW;
      drawH = stageW / imgRatio;
    } else {
      drawH = stageH;
      drawW = stageH * imgRatio;
    }
    const offsetX = (stageW - drawW) / 2;
    const offsetY = (stageH - drawH) / 2;

    // selection 视觉 W/H (CSS aspect-ratio 16/9)
    const visualW = this.crop.w * stageW;
    const visualH = visualW * (9 / 16);

    // 视觉选区在 stage 内的像素 X/Y (相对于 stage 左上角)
    const visualX = this.crop.x * stageW;
    const visualY = this.crop.y * stageH;
    return {
      // canvas 输出尺寸 = selection 视觉 W/H (跟视觉一致)
      outW: visualW,
      outH: visualH,
      // 原图坐标系剪裁源 (canvas drawImage src)
      // Math.max(0, ...) 兜底: 选区超出 image 实际渲染区时 srcX/srcY 可能是负数
      srcX: Math.max(0, (visualX - offsetX) * (imgW / drawW)),
      srcY: Math.max(0, (visualY - offsetY) * (imgH / drawH)),
      srcW: visualW * (imgW / drawW),
      srcH: visualH * (imgH / drawH),
      drawW,
      drawH,
      offsetX,
      offsetY
    };
  }

  /**
   * 应用剪裁 — 从 image 取 crop 选区 → canvas → toBlob
   */
  applyCrop() {
    return new Promise((resolve, reject) => {
      if (!this.image || !this.stageRef) {
        reject(new Error('Image or stage not ready'));
        return;
      }
      const stageRect = this.stageRef.getBoundingClientRect();
      const stageW = stageRect.width;
      const stageH = stageRect.height;
      const rect = this.getCropPixelRect(stageW, stageH);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(rect.srcW);
      canvas.height = Math.round(rect.srcH);
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.image, rect.srcX, rect.srcY, rect.srcW, rect.srcH, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Canvas toBlob failed'));
          return;
        }
        resolve(blob);
      }, OUTPUT_TYPE, OUTPUT_QUALITY);
    });
  }

  /**
   * Phase D2: 上传裁剪结果, 返回可访问 URL。
   *
   * 改造前是硬编码 COS 直传 (取 /api/ziven/core/cos/signature → 拼 FormData →
   * POST 到 {bucket}-{app_id}.cos.{region}.myqcloud.com → 三段 CDN fallback),
   * 现在整段委托给本扩展的 `common/helpers/uploadFile`。
   * 顺带修掉一个**静默失败**: 旧代码 `await fetch(uploadUrl, {method:'POST', body:formData})`
   * **从不检查 response.status** —— COS 回 403 时 fetch 一样 resolve, 于是用户拿到一个
   * 打不开的 URL, 且没有任何报错。共享方法里有 `xhrUpload` 的 2xx 判定。
   *
   * ⚠️ 入参是 canvas.toBlob() 出来的 **Blob, 不是 File** —— Blob 没有 `.name`。
   *    所以必须显式传 `fileName`, 否则本地档那条 multipart 的 filename 会是空,
   *    而 COS 档的 key 会掉成 `.bin`。
   * ⚠️ 保持 `.jpg`: HeroImageCropModal 的 onApply 用的是
   *    `canvas.toBlob(cb, 'image/jpeg', OUTPUT_QUALITY)`, 产出就是 JPEG。
   */
  async uploadToCOS(blob) {
    return uploadFile(blob, {
      prefix: 'hero',
      // buildKey 只取它的**扩展名**(它自己生成 ts_rand), 这里传带时间戳的名字
      // 只是为了让本地档那条 multipart 的 filename 也可读。
      fileName: "hero_".concat(Date.now(), ".jpg")
    });
  }
}
flarum.reg.add('ziiven-ziven-core', 'forum/components/HeroImageCropModal', HeroImageCropModal);